/**
 * Admin Groups Management Page (flattened — spec-130 follow-up).
 *
 * Folders are no longer surfaced in the UI. Each group lives under
 * the chapter's default General folder, resolved server-side from
 * the chapterId passed on create. The admin picks a chapter and
 * manages a flat list of groups.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useIsSuperAdmin } from '@/hooks/useAuth';
import {
  ApiClientError,
  chaptersApi,
  groupsApi,
  type ChapterSummary,
  type GroupJoinPolicy,
  type GroupVisibility,
} from '@/services/api';
import type { Group } from '@/types';

function GroupBadges({ group }: { group: Group }) {
  return (
    <div className="flex flex-wrap gap-2">
      {group.isDefault && (
        <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
          Default
        </span>
      )}
      <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/70 border border-white/10">
        {group.visibility ?? 'listed'}
      </span>
      <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/70 border border-white/10">
        {group.joinPolicy ?? 'open'}
      </span>
      {group.aiModeration && (
        <span className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
          AI Moderated
        </span>
      )}
      {group.requireApproval && (
        <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          Approval Required
        </span>
      )}
      {!group.allowMemberPosts && (
        <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">
          Admin Only
        </span>
      )}
    </div>
  );
}

export function GroupsPage() {
  // Reserved: useIsSuperAdmin lets us tweak labels in future. For now
  // we always render a chapter picker.
  useIsSuperAdmin();
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [chapterId, setChapterId] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Group | null>(null);
  const [creating, setCreating] = useState(false);

  // Load chapters once.
  useEffect(() => {
    let cancelled = false;
    chaptersApi.list('navarasam').then((rows) => {
      if (cancelled) return;
      setChapters(rows);
      if (rows.length > 0 && !chapterId) setChapterId(rows[0]!.id);
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load chapters');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await groupsApi.list();
      setGroups(rows);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleDelete(g: Group) {
    if (g.isDefault) {
      window.alert('Cannot delete the default General group.');
      return;
    }
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try {
      await groupsApi.delete(g.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-white">Groups</h1>
          <p className="text-white/60 text-sm mt-1">
            Per-chapter discussion groups. Folders are hidden from the UI; every group
            lives under the chapter's General folder.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-white/40 mb-1">Create in chapter</label>
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              className="input py-1 px-2 text-sm w-auto"
            >
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={!chapterId}
            className="btn-primary text-sm px-3 py-1.5"
          >
            + Group
          </button>
        </div>
      </header>

      {error && (
        <div className="card border-red-500/20 bg-red-500/10 text-red-300 text-sm">{error}</div>
      )}

      {loading ? (
        <p className="text-white/60">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="card text-center py-10 text-white/60">
          <p className="text-lg text-white mb-1">No groups yet</p>
          <p className="text-sm">Tap “+ Group” to create the first one for this chapter.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {groups.map((g) => (
            <li key={g.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-white font-semibold">{g.name}</h3>
                {g.description && (
                  <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{g.description}</p>
                )}
                <div className="mt-1.5"><GroupBadges group={g} /></div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/admin/groups/${g.id}/members`} className="text-sm text-white/60 hover:text-white px-2 py-1">
                  Members
                </Link>
                <button
                  type="button"
                  onClick={() => setEditing(g)}
                  className="text-sm text-white/60 hover:text-white px-2 py-1"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(g)}
                  disabled={g.isDefault}
                  title={g.isDefault ? 'Cannot delete default group' : undefined}
                  className="text-sm text-red-400 hover:text-red-300 px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <GroupModal
          chapterId={chapterId}
          group={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); void reload(); }}
        />
      )}
    </div>
  );
}

function GroupModal({
  chapterId,
  group,
  onClose,
  onSaved,
}: {
  chapterId: string;
  group: Group | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [allowMemberPosts, setAllowMemberPosts] = useState(group?.allowMemberPosts ?? true);
  const [requireApproval, setRequireApproval] = useState(group?.requireApproval ?? false);
  const [aiModeration, setAiModeration] = useState(group?.aiModeration ?? false);
  const [visibility, setVisibility] = useState<GroupVisibility>(group?.visibility ?? 'listed');
  const [joinPolicy, setJoinPolicy] = useState<GroupJoinPolicy>(group?.joinPolicy ?? 'open');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Group name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (group) {
        await groupsApi.update(group.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          allowMemberPosts,
          requireApproval,
          aiModeration,
          visibility,
          joinPolicy,
        });
      } else {
        await groupsApi.create({
          // Backend resolves chapter → General folder.
          // groupsApi.create's type union accepts either folderId or chapterId.
          chapterId,
          name: name.trim(),
          description: description.trim() || undefined,
          allowMemberPosts,
          requireApproval,
          aiModeration,
          visibility,
          joinPolicy,
        } as Parameters<typeof groupsApi.create>[0]);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        className="card max-w-lg w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{group ? 'Edit group' : 'Create group'}</h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div>
          <label className="block text-xs text-white/60 mb-1">Name</label>
          <input className="input" value={name} maxLength={255} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Description</label>
          <textarea className="input" rows={3} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as GroupVisibility)}
              className="input py-1.5"
            >
              <option value="listed">Listed — visible to chapter members</option>
              <option value="unlisted">Unlisted — only members see it</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Join policy</label>
            <select
              value={joinPolicy}
              onChange={(e) => setJoinPolicy(e.target.value as GroupJoinPolicy)}
              className="input py-1.5"
              disabled={joinPolicy === 'inherit_chapter' && !!group?.isDefault}
            >
              {(joinPolicy === 'inherit_chapter' || group?.isDefault) && (
                <option value="inherit_chapter">Inherit chapter — everyone in chapter</option>
              )}
              <option value="open">Open — one-click join</option>
              <option value="request">Request — admin approves</option>
              <option value="closed">Closed — admin adds members</option>
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={allowMemberPosts} onChange={(e) => setAllowMemberPosts(e.target.checked)} />
            Allow member posts
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />
            Require admin approval for member posts
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={aiModeration} onChange={(e) => setAiModeration(e.target.checked)} />
            AI moderation
          </label>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button type="submit" disabled={saving || !name.trim()} className="btn-primary text-sm">
            {saving ? 'Saving…' : group ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GroupsPage;
